/** ACTIVITY_STATUS is intentionally NOT moved out: its values appear in migration CHECK constraints and QUAL-MIGRATION-ENUM-SSOT-001 scans the const leaf text. */

import { WRANGLER_ENV_KEYS } from './config-environments.ts';

/* The stack-trace character cap left this leaf 2026-08-07. It lived here as `MAX_STACK_TRACE_LENGTH` beside nine
 * unit-less sibling caps while its two LINE-unit counterparts sat in `const/data-sanitizer.ts` and in a default
 * parameter -- see `STACK_TRACE_CAPS.MAX_CHARS` (`const/stack-trace-caps.ts`), which now owns all three. Those nine
 * siblings went the same way on 2026-08-13, each left with no consumer but its own sibling test
 * (GOV-DEAD-CODE-001). */
export const MAX_DESCRIPTION_LENGTH = 100;

/** Default version stamp applied to activity records when no explicit version is provided. */
export const DEFAULT_ACTIVITY_VERSION = '1.0';

/**
 * The fleet's `head_sampling_rate` TIER VOCABULARY for wrangler observability blocks: the closed set of values a config
 * may declare. The tiers were authored as a deliberate set (commit fb7530c7bd1) but lived only as repeated jsonc
 * literals, so nothing named them and a typo'd fourth rate read as a choice; this leaf is that name, and
 * `INFRA-OBSERVABILITY-TRACES-001` enforces membership against it (a jsonc config cannot import, so the rule is the
 * only possible pairing). Measured live across every declaring config at authoring: the set below is exhaustive.
 *
 * VOCABULARY ONLY. Which tier a given service belongs on is a PENDING OPERATOR DECISION, recorded 2026-08-09 in the
 * `fix` text of `INFRA-OBSERVABILITY-TRACES-001`; nothing here maps a service to a tier, declares a default, or may be
 * read as ranking them. Adding a fourth tier is an operator decision taken HERE first -- which is the point of naming
 * the set at all.
 */
export const OBSERVABILITY_SAMPLING_TIERS = {
	/** 100% -- every request sampled. */
	FULL: 1,
	/** 5%. */
	REDUCED: 0.05,
	/** 1%. */
	MINIMAL: 0.01,
} as const;

/**
 * The fleet's `observability.traces.destinations` DATASET VOCABULARY, keyed by the wrangler `env.<key>` block that must
 * declare it. Sibling of `OBSERVABILITY_SAMPLING_TIERS` above and governed by the same rule
 * (`INFRA-OBSERVABILITY-TRACES-001`): 60 literals across 30 configs named the two trace datasets with nothing owning
 * the spelling, so a typo'd destination is not a build error -- the worker deploys and its spans land in a dataset
 * nobody queries, which reads identically to a service that emits nothing.
 *
 * KEYED BY `WRANGLER_ENV_KEYS` rather than by a free-standing DEV/PROD pair so BOTH arms of the paired rule derive from
 * ONE record and neither restates the env-to-dataset pairing: membership is `Object.values(...)`, alignment is a lookup
 * by the declaring scope's env name. A jsonc config cannot import this leaf, so the rule is the only possible pairing.
 * Wrangler env inheritance REPLACES an inherited key wholesale, which is why every config declares observability PER
 * ENV and a top-level declaration -- serving both envs at once -- is left unaligned by construction.
 */
export const OBSERVABILITY_TRACE_DESTINATIONS = {
	[WRANGLER_ENV_KEYS.DEV]: 'federa-flow-traces-dev',
	[WRANGLER_ENV_KEYS.PRODUCTION]: 'federa-flow-traces-prod',
} as const;
